import React, { useState, useEffect, useRef } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { EXTENDED_DATA_SCHEMA_TYPES, LISTING_STATE_DRAFT, propTypes } from '../../../../util/types';
import {
  isFieldForCategory,
  isFieldForListingType,
  isValidCurrencyForTransactionProcess,
} from '../../../../util/fieldHelpers';
import { maxLength, required, composeValidators } from '../../../../util/validators';

// Import shared components
import {
  Form,
  Button,
  FieldSelect,
  FieldTextInput,
  Heading,
  CustomExtendedDataField,
} from '../../../../components';
// Import modules from this directory
import css from './EditListingDestinationsForm.module.css';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import axios from 'axios';
import { keyToString, formatLocationString } from '../../../../util/data';
import OutsideClickHandler from '../../../../components/OutsideClickHandler/OutsideClickHandler';
import { countryToContinentMap, CUSTOM_REGION_COUNTRIES } from '../../../../constants';

const TITLE_MAX_LENGTH = 60;
const getSupportedCountryCodes = supportedCountries => supportedCountries.map(c => c.code);

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

// Field component that either allows selecting listing type (if multiple types are available)
// or just renders hidden fields:
// - listingType              Set of predefined configurations for each listing type
// - transactionProcessAlias  Initiate correct transaction against Marketplace API
// - unitType                 Main use case: pricing unit
const FieldSelectListingType = props => {
  const {
    name,
    listingTypes,
    hasExistingListingType,
    onListingTypeChange,
    formApi,
    formId,
    intl,
  } = props;
  const hasMultipleListingTypes = listingTypes?.length > 1;

  const handleOnChange = value => {
    const selectedListingType = listingTypes.find(config => config.listingType === value);
    formApi.change('transactionProcessAlias', selectedListingType.transactionProcessAlias);
    formApi.change('unitType', selectedListingType.unitType);

    if (onListingTypeChange) {
      onListingTypeChange(selectedListingType);
    }
  };
  const getListingTypeLabel = listingType => {
    const listingTypeConfig = listingTypes.find(config => config.listingType === listingType);
    return listingTypeConfig ? listingTypeConfig.label : listingType;
  };

  return hasMultipleListingTypes && !hasExistingListingType ? (
    <>
      <FieldSelect
        id={formId ? `${formId}.${name}` : name}
        name={name}
        className={css.listingTypeSelect}
        label={intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
        validate={required(
          intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeRequired' })
        )}
        onChange={handleOnChange}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypePlaceholder' })}
        </option>
        {listingTypes.map(config => {
          const type = config.listingType;
          return (
            <option key={type} value={type}>
              {config.label}
            </option>
          );
        })}
      </FieldSelect>
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  ) : hasMultipleListingTypes && hasExistingListingType ? (
    <div className={css.listingTypeSelect}>
      <Heading as="h5" rootClassName={css.selectedLabel}>
        {intl.formatMessage({ id: 'EditListingDetailsForm.listingTypeLabel' })}
      </Heading>
      <p className={css.selectedValue}>{getListingTypeLabel(formApi.getFieldState(name)?.value)}</p>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </div>
  ) : (
    <>
      <FieldHidden name={name} />
      <FieldHidden name="transactionProcessAlias" />
      <FieldHidden name="unitType" />
    </>
  );
};

// Finds the correct subcategory within the given categories array based on the provided categoryIdToFind.
const findCategoryConfig = (categories, categoryIdToFind) => {
  return categories?.find(category => category.id === categoryIdToFind);
};

/**
 * Recursively render subcategory field inputs if there are subcategories available.
 * This function calls itself with updated props to render nested category fields.
 * The select field is used for choosing a category or subcategory.
 */
const CategoryField = props => {
  const { currentCategoryOptions, level, values, prefix, handleCategoryChange, intl } = props;

  const currentCategoryKey = `${prefix}${level}`;

  const categoryConfig = findCategoryConfig(currentCategoryOptions, values[`${prefix}${level}`]);

  return (
    <>
      {currentCategoryOptions ? (
        <FieldSelect
          key={currentCategoryKey}
          id={currentCategoryKey}
          name={currentCategoryKey}
          className={css.listingTypeSelect}
          onChange={event => handleCategoryChange(event, level, currentCategoryOptions)}
          label={intl.formatMessage(
            { id: 'EditListingDetailsForm.categoryLabel' },
            { categoryLevel: currentCategoryKey }
          )}
          validate={required(
            intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryRequired' },
              { categoryLevel: currentCategoryKey }
            )
          )}
        >
          <option disabled value="">
            {intl.formatMessage(
              { id: 'EditListingDetailsForm.categoryPlaceholder' },
              { categoryLevel: currentCategoryKey }
            )}
          </option>

          {currentCategoryOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </FieldSelect>
      ) : null}

      {categoryConfig?.subcategories?.length > 0 ? (
        <CategoryField
          currentCategoryOptions={categoryConfig.subcategories}
          level={level + 1}
          values={values}
          prefix={prefix}
          handleCategoryChange={handleCategoryChange}
          intl={intl}
        />
      ) : null}
    </>
  );
};

const FieldSelectCategory = props => {
  useEffect(() => {
    checkIfInitialValuesExist();
  }, []);

  const { prefix, listingCategories, formApi, intl, setAllCategoriesChosen, values } = props;

  // Counts the number of selected categories in the form values based on the given prefix.
  const countSelectedCategories = () => {
    return Object.keys(values).filter(key => key.startsWith(prefix)).length;
  };

  // Checks if initial values exist for categories and sets the state accordingly.
  // If initial values exist, it sets `allCategoriesChosen` state to true; otherwise, it sets it to false
  const checkIfInitialValuesExist = () => {
    const count = countSelectedCategories(values, prefix);
    setAllCategoriesChosen(count > 0);
  };

  // If a parent category changes, clear all child category values
  const handleCategoryChange = (category, level, currentCategoryOptions) => {
    const selectedCatLenght = countSelectedCategories();
    if (level < selectedCatLenght) {
      for (let i = selectedCatLenght; i > level; i--) {
        formApi.change(`${prefix}${i}`, null);
      }
    }
    const categoryConfig = findCategoryConfig(currentCategoryOptions, category).subcategories;
    setAllCategoriesChosen(!categoryConfig || categoryConfig.length === 0);
  };

  return (
    <CategoryField
      currentCategoryOptions={listingCategories}
      level={1}
      values={values}
      prefix={prefix}
      handleCategoryChange={handleCategoryChange}
      intl={intl}
    />
  );
};

// Add collect data for listing fields (both publicData and privateData) based on configuration
const AddListingFields = props => {
  const { listingType, listingFieldsConfig, selectedCategories, formId, intl } = props;
  const targetCategoryIds = Object.values(selectedCategories);

  const fields = listingFieldsConfig.reduce((pickedFields, fieldConfig) => {
    const { key, schemaType, scope } = fieldConfig || {};
    const namespacedKey = scope === 'public' ? `pub_${key}` : `priv_${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isProviderScope = ['public', 'private'].includes(scope);
    const isTargetListingType = isFieldForListingType(listingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    return isKnownSchemaType && isProviderScope && isTargetListingType && isTargetCategory
      ? [
        ...pickedFields,
        <CustomExtendedDataField
          key={namespacedKey}
          name={namespacedKey}
          fieldConfig={fieldConfig}
          defaultRequiredMessage={intl.formatMessage({
            id: 'EditListingDetailsForm.defaultRequiredMessage',
          })}
          formId={formId}
        />,
      ]
      : pickedFields;
  }, []);

  return <>{fields}</>;
};

// Form that asks title, description, transaction process and unit type for pricing
// In addition, it asks about custom fields according to marketplace-custom-config.js
const EditListingDestinationsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        autoFocus,
        className,
        disabled,
        ready,
        formId,
        form: formApi,
        handleSubmit,
        intl,
        invalid,
        pristine,
        marketplaceCurrency,
        saveActionMsg,
        draftButtonText,
        updated,
        updateInProgress,
        saveAsDraftInProgress,
        fetchErrors,
        listingCurrency,
        values,
        otherListings
      } = formRenderProps;

      const { destinations: storedDestinations = [], destinationCountries: storeDestinationCountries = [], destinationRegions: storeDestinationRegions = [], subDestinations: subStoreDestinations = [] } = values || {};
      const [destinations, setDestinations] = useState(storedDestinations);

      const { listingType, transactionProcessAlias, unitType, state } = values;
      const mapboxToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

      const [countryInput, setCountryInput] = useState('');
      const [countryInputFocused, setCountryInputFocused] = useState(false);
      const countryDropdownRef = useRef(null);
      const showPredictions = countryInputFocused;
      const [predictions, setPredictions] = useState([]);


      // Sub Destinations
      const [subDestinations, setSubDestinations] = useState(subStoreDestinations);
      const [subDestinationInputFocused, setDestinationInputFocused] = useState(false);
      const DestinationDropdownRef = useRef(null);
      const [destinationPredictions, setDestinationPredictions] = useState([]);

      const fetchPredictions = async (query, includeRegion = true) => {
        const searchType = includeRegion ? "country,region" : "country"
        try {
          const urlV6 = `https://api.mapbox.com/search/geocode/v6/forward?q=${query}&access_token=${mapboxToken}`
          const response = await axios.get(
            urlV6,
            {
              params: {
                types: searchType
              }
            }
          );

          setPredictions(prev => {
            const predictions = response.data.features.filter(({ properties: { full_address } }) => {
              return !destinations.includes(formatLocationString(full_address, '_'));
            });
            return predictions;
          });
        } catch (error) {
          console.error('Error fetching predictions:', error);
        }
      };


      function getUniqueByRegionName(features) {
        const seenRegions = new Set();
        const uniqueFeatures = [];

        for (const feature of features) {
          const regionName = feature?.properties?.context?.region?.name;
          if (regionName && !seenRegions.has(regionName)) {
            seenRegions.add(regionName);
            uniqueFeatures.push(feature);
          }
        }

        return uniqueFeatures;
      }

      // Sub Destinations predictions
      const fetchDestinationPredictions = async query => {
        try {
          const destinations = values?.destinationCountries;
          const limitSearch = (Array.isArray(destinations) && destinations[0].country_code) ? [destinations[0].country_code] : [];
          // Return early if no country is selected
          if (limitSearch.length === 0) {
            setDestinationPredictions([]);
            return;
          }
          const urlV6 = `https://api.mapbox.com/search/geocode/v6/forward?q=${query}&access_token=${mapboxToken}${Array.isArray(limitSearch) && limitSearch.length ? "&country=" + limitSearch.join(",") : ""}`
          const response = await axios.get(
            urlV6
          );

          setDestinationPredictions(prev => {
            const predictions = response.data.features.filter(({ properties: { full_address } }) => {
              return !destinations.includes(formatLocationString(full_address, '_'));
            });

            const uniquePredictions = getUniqueByRegionName(predictions) || [];
            return uniquePredictions;
          });
        } catch (error) {
          console.error('Error fetching predictions:', error);
        }
      };

      useEffect(() => {
        if (countryInput) {
          fetchPredictions(countryInput, false);
        } else {
          setPredictions([]);
        }
      }, [countryInput]);

      const handleInputChange = e => {
        formApi.change('destination', e.target.value);
        setCountryInput(e.target.value);
      };

      const extractPredictionData = prediction => {
        const { feature_type, context = {} } = prediction?.properties || {};
        const { region, country } = context || {};

        let continent = null
        for (const [key, value] of Object.entries(CUSTOM_REGION_COUNTRIES)) {
          if (value.find(countryCode => countryCode === country?.country_code)) {
            continent = key;
          }
        }

        let place_name = '';
        if (feature_type === 'country') {
          place_name = country.name;
          // return { country: country.name, place_name, continent };
        }

        if (region) {
          place_name = place_name.length
            ? `${place_name}, ${region.name}, ${country.name}`
            : `${region.name}, ${country.name}`;
        }

        return { country, place_name, continent };
      };

      const handlePredictionClick = prediction => {
        const { country, place_name, continent } = extractPredictionData(prediction);
        setDestinations(prev => {
          const payload = [...new Set([
            // ...prev,
            place_name
          ])].map(item =>
            item.includes('_') ? item : formatLocationString(item, '_')
          );
          const destinationCountries = [
            ...new Set([
              // ...storeDestinationCountries,
              // ...(Array.isArray(values?.destinationCountries) ? values.destinationCountries : []),
              country,
            ]),
          ].filter(Boolean);
          const destinationRegions = [
            ...new Set([
              // ...storeDestinationRegions,
              // ...(Array.isArray(values?.destinationRegions) ? values.destinationRegions : []),
              continent,
            ]),
          ].filter(Boolean);

          formApi.batch(() => {
            formApi.change('destinations', payload);
            formApi.change('destinationCountries', destinationCountries);
            formApi.change('destinationRegions', destinationRegions);
            formApi.change('destination', '');
            formApi.change('subDestinations', []);
          });
          setSubDestinations([])
          setDestinationPredictions([]); // clear predictions after change in country
          setCountryInput('');
          return payload;
        });
      };
      const getSubDestinationDetails = (prediction) => {
        const { properties } = prediction || {};
        const { context = {}, coordinates, full_address } = properties || {};
        const { country, region } = context;

        const regionName = region?.name;
        const countryName = country?.name;
        const formattedLocation = [regionName, countryName].filter(Boolean).join(', ');

        return {
          coordinates,
          full_address: formattedLocation,
          countryName,
          countryCode: country?.country_code,
          regionName,
          regionCode: region?.region_code,
          regionFilter: countryName && regionName ? `${countryName}_${regionName}` : undefined
        };
      };

      function addUniqueSubDestination(subDestinations, prediction) {
        const destinationData = getSubDestinationDetails(prediction);

        // Ensure subDestinations is always an array
        const allSubDestinations = [
          ...(Array.isArray(subDestinations) ? subDestinations : []),
          destinationData,
        ];

        // Deduplicate based on `regionFilter`
        const uniqueSubDestinations = Object.values(
          allSubDestinations.reduce((acc, curr) => {
            acc[curr.regionFilter] = curr;
            return acc;
          }, {})
        );
        return uniqueSubDestinations;
      }


      const handleSubDestinationPredictionClick = prediction => {
        // const destinationData = getSubDestinationDetails(prediction);
        // const newSubDestinations = [
        //   ...new Set([
        //     ...(Array.isArray(subDestinations) ? subDestinations : []),
        //     destinationData,
        //   ]),
        // ].filter(Boolean);

        const newSubDestinations = addUniqueSubDestination(subDestinations, prediction);
        setSubDestinations(newSubDestinations);
        formApi.batch(() => {
          formApi.change('subDestination', "");
          formApi.change('subDestinations', newSubDestinations);
        });
        setDestinationInputFocused(false);
      };

      const handleMouseEnter = index => {
        const items = countryDropdownRef.current.querySelectorAll('li');
        items.forEach((item, idx) => {
          item.classList.toggle(css.highlighted, idx === index);
        });
      };

      // Determine the currency to validate:
      // - If editing an existing listing, use the listing's currency.
      // - If creating a new listing, fall back to the default marketplace currency.
      const currencyToCheck = listingCurrency || marketplaceCurrency;

      // Verify if the selected listing type's transaction process supports the chosen currency.
      // This checks compatibility between the transaction process
      // and the marketplace or listing currency.
      const isCompatibleCurrency = isValidCurrencyForTransactionProcess(
        transactionProcessAlias,
        currencyToCheck
      );

      const checkSameCountryListing = Array.isArray(otherListings) && otherListings.length > 0
        ? otherListings.some(listing => {
          const listingDestinations = listing?.attributes?.publicData?.destinations;
          if (!Array.isArray(listingDestinations) || !Array.isArray(destinations)) return false;
          return listingDestinations.some(dest => destinations.includes(dest));
        })
        : false;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const hasMandatoryListingTypeData = listingType && transactionProcessAlias && unitType;
      const submitDisabled =
        invalid ||
        checkSameCountryListing ||
        disabled ||
        submitInProgress ||
        !hasMandatoryListingTypeData ||
        !destinations.length ||
        !isCompatibleCurrency;
      const destinationLength = values?.destination?.length || 0;

      const destinationsCountry = (Array.isArray(values?.destinationCountries) && values?.destinationCountries.length && values?.destinationCountries[0].country_code) ? values?.destinationCountries[0].name : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <ErrorMessage fetchErrors={fetchErrors} />
          <div>
            <label>{intl.formatMessage({ id: 'EditListingDetailsForm.destinations' })}</label>
            <div className={css.destinationsContainer}>
              <div className={css.inputBox}>
                <FieldTextInput
                  id={`${formId}destination`}
                  name="destination"
                  className={css.titleInput}
                  maxLength={TITLE_MAX_LENGTH}
                  onChange={handleInputChange}
                  onFocus={() => setCountryInputFocused(true)}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'EditListingDetailsForm.sloganPlaceholder',
                  })}
                />
                {predictions.length > 0 && showPredictions && (
                  <OutsideClickHandler className={css.dropdown} onOutsideClick={() => setCountryInputFocused(false)}>
                    <ul ref={countryDropdownRef}>
                      {predictions.map((prediction, index) => (
                        <li
                          key={`${prediction.id}-${index}`}
                          onClick={e => {
                            e.preventDefault();
                            handlePredictionClick(prediction);
                            setCountryInputFocused(false);
                          }}
                          onMouseEnter={() => handleMouseEnter(index)}
                        >
                          {prediction.properties.full_address}
                        </li>
                      ))}
                    </ul>
                  </OutsideClickHandler>
                )}
              </div>
            </div>
          </div>

          <div className={css.destinationsList}>
            {destinations.map(destination => (
              <div key={destination} className={css.destinationItem}>
                <div className={css.destinationItemText}>
                  {' '}
                  <span className={css.destinationItemTextIcon}></span> {keyToString(destination)}
                </div>{' '}
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setDestinations(prev => {
                      const payload = prev.filter(elm => elm !== destination);
                      formApi.change('destinations', payload);
                      return payload;
                    })
                    setSubDestinations([]);
                  }
                  }
                >
                  <IconCollection icon="icon-cross-circle-black-italic" />
                </div>
              </div>
            ))}

            {checkSameCountryListing ? <div className={css.checkSameCountryListing}>
              You already have a listing for: {Array.isArray(destinations) && destinations.join(", ")}.
              Expert should only be able to make one listing per country
            </div> : null}
          </div>




          {destinationsCountry ? <>
            <div>
              <label>{intl.formatMessage({ id: 'EditListingDetailsForm.subDestinationLabel' }, { country: destinationsCountry })}</label>
              <div className={css.destinationsContainer}>
                <div className={css.inputBox}>
                  <FieldTextInput
                    id={`${formId}destination`}
                    name="subDestination"
                    className={css.titleInput}
                    maxLength={TITLE_MAX_LENGTH}
                    disabled={checkSameCountryListing}
                    onChange={(e) => {
                      formApi.change('subDestination', e.target.value);
                      fetchDestinationPredictions(e.target.value);
                    }}
                    onFocus={() => {
                      setDestinationInputFocused(true)
                    }}
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.subDestinationPlaceholder',
                    })}
                  />
                  {destinationPredictions.length > 0 && subDestinationInputFocused && (
                    <OutsideClickHandler className={css.dropdown} onOutsideClick={() => setDestinationInputFocused(false)}>
                      <ul ref={DestinationDropdownRef}>
                        {destinationPredictions.map((prediction, index) => (
                          <li
                            key={`${prediction.id}-${index}`}
                            onClick={e => {
                              e.preventDefault();
                              handleSubDestinationPredictionClick(prediction);
                              setDestinationInputFocused(false);
                            }}
                            onMouseEnter={() => {
                              const items = DestinationDropdownRef.current.querySelectorAll('li');
                              items.forEach((item, idx) => {
                                item.classList.toggle(css.highlighted, idx === index);
                              });
                            }}
                          >
                            {/* {prediction.properties.place_formatted} */}
                            {prediction.properties?.context?.region?.name ? `${prediction.properties.context.region.name}, ` : ''}{prediction.properties?.context?.country?.name}
                          </li>
                        ))}
                      </ul>
                    </OutsideClickHandler>
                  )}
                </div>
              </div>
            </div>

            <div className={css.destinationsList}>
              {subDestinations.filter(
                (destination, index, self) =>
                  index == self.findIndex((d) => d.full_address == destination.full_address)
              ).map((destination, index) => (
                <div key={`${destination.full_address}-${index}`} className={css.destinationItem}>
                  <div className={css.destinationItemText}>
                    {' '}
                    <span className={css.destinationItemTextIcon}></span> {destination.full_address}
                  </div>{' '}
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      // Remove ALL entries with the same full_address
                      const payload = subDestinations.filter(
                        (d) => d.full_address !== destination.full_address
                      );
                      formApi.change('subDestinations', payload);
                      setSubDestinations(payload);
                    }
                    }
                  >
                    <IconCollection icon="icon-cross-circle-black-italic" />
                  </div>
                </div>
              ))}
            </div>
          </> : null}

          <div className={css.submitButtonContainer}>
            <Button
              className={css.submitButton}
              type="submit"
              onClick={() => formApi.change('draftButtonClicked', false)}
              inProgress={submitInProgress && !values?.draftButtonClicked}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>
            {state === LISTING_STATE_DRAFT && (
              <Button
                className={css.draftButton}
                type="submit"
                onClick={() =>
                  formApi.batch(() => {
                    formApi.change('saveDraftOnly', true);
                    formApi.change('draftButtonClicked', true);
                  })
                }
                inProgress={saveAsDraftInProgress}
                disabled={submitDisabled}
                ready={submitReady || values?.draftButtonClicked}
              >
                {draftButtonText}
              </Button>
            )}
          </div>
        </Form>
      );
    }}
  />
);

EditListingDestinationsFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingDetailsForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingDestinationsFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  pickSelectedCategories: func.isRequired,
  selectableListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionProcessAlias: string.isRequired,
      unitType: string.isRequired,
    })
  ).isRequired,
  hasExistingListingType: bool,
  listingFieldsConfig: propTypes.listingFields,
};

export default compose(injectIntl)(EditListingDestinationsFormComponent);
